import { Axis, Vertex3 } from '@rta/rta-blueprint-library';

import { DimensionLine } from '@r-components';
import { type FlatTrimPiece, TRIM_HEIGHT_DIMENSION_OFFSET, TRIM_STEP_DIMENSION_GAP, TRIM_WIDTH_DIMENSION_GAP } from './helpers';
import { RenderFlatTrimDimension } from './render-flat-trim-dimension';

type RenderFlatTrimGroupDimensionsProps = {
	piece: FlatTrimPiece;
	baselineY: number;
	includeLabelBox: boolean;
	dimensionFontSize: number;
	useFractions: boolean;
};

export const RenderFlatTrimGroupDimensions = (props: RenderFlatTrimGroupDimensionsProps): React.ReactElement => {
	const { piece, baselineY, includeLabelBox, dimensionFontSize, useFractions } = props;
	const { width, halfWidth, leftHeight, rightHeight, isStepped, x, tallHeight } = piece;

	// an unstepped piece carries a single height dimension, which the shipped sheet puts on the right
	const firstHeightX = isStepped
		? x - (width / 2) - TRIM_HEIGHT_DIMENSION_OFFSET
		: x + (width / 2) + TRIM_HEIGHT_DIMENSION_OFFSET;

	return (
		<>
			{/* left half height, or the only height when unstepped */}
			<DimensionLine
				length={leftHeight}
				axis={new Axis({ dataIn: { x: 0, y: 0, z: 90 }})}
				includeLabelBox={includeLabelBox}
				bBox={{
					origin: new Vertex3({ dataIn: { x: firstHeightX, y: baselineY - (leftHeight / 2), z: 0 }}),
					size: { width: 0, height: leftHeight, depth: 0 }
				}}
				padding={0}
				indicatorStart="flat"
				indicatorEnd="flat"
				level={true}
			/>

			{/* right half height, only when the halves differ */}
			{isStepped && <DimensionLine
				length={rightHeight}
				axis={new Axis({ dataIn: { x: 0, y: 0, z: 90 }})}
				includeLabelBox={includeLabelBox}
				bBox={{
					origin: new Vertex3({ dataIn: { x: x + (width / 2) + TRIM_HEIGHT_DIMENSION_OFFSET, y: baselineY - (rightHeight / 2), z: 0 }}),
					size: { width: 0, height: rightHeight, depth: 0 }
				}}
				padding={0}
				indicatorStart="flat"
				indicatorEnd="flat"
				level={true}
			/>}

			{/* overall width, clear below the baseline */}
			<RenderFlatTrimDimension
				length={width}
				centerX={x}
				lineY={baselineY + TRIM_WIDTH_DIMENSION_GAP}
				labelBelow={true}
				fontSize={dimensionFontSize}
				includeLabelBox={includeLabelBox}
				useFractions={useFractions}
			/>

			{/* half width, clear above the stepped shoulder and below the title */}
			{isStepped && <RenderFlatTrimDimension
				length={halfWidth}
				centerX={x + (leftHeight > rightHeight ? -(halfWidth / 2) : (halfWidth / 2))}
				lineY={baselineY - tallHeight - TRIM_STEP_DIMENSION_GAP}
				labelBelow={false}
				fontSize={dimensionFontSize}
				includeLabelBox={includeLabelBox}
				useFractions={useFractions}
			/>}
		</>
	);
};
